import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash, ShoppingCart, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatPrice, tierPrice } from "../lib/api";
import { useCart } from "../context/CartContext";

const emptyRow = () => ({ sku: "", qty: 1, status: null, product: null });

export default function QuickOrderPage() {
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  const [checking, setChecking] = useState(false);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const setRow = (i, patch) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const check = async () => {
    const filled = rows.filter((r) => r.sku.trim());
    if (filled.length === 0) { toast.error("Ievadiet vismaz vienu artikula numuru"); return; }
    setChecking(true);
    try {
      const { data } = await api.post("/products/lookup", { items: filled.map((r) => ({ sku: r.sku.trim(), qty: r.qty })) });
      const bySku = {};
      data.forEach((d) => { bySku[d.sku.toLowerCase()] = d; });
      setRows((prev) => prev.map((r) => {
        if (!r.sku.trim()) return r;
        const res = bySku[r.sku.trim().toLowerCase()];
        return { ...r, status: res?.found ? "found" : "notfound", product: res?.product || null };
      }));
    } catch {
      toast.error("Neizdevās pārbaudīt artikulus");
    } finally {
      setChecking(false);
    }
  };

  const addAll = () => {
    const found = rows.filter((r) => r.status === "found" && r.product);
    if (found.length === 0) { toast.error("Vispirms pārbaudiet artikulus"); return; }
    found.forEach((r) => addItem(r.product, r.qty));
    toast.success(`Grozam pievienotas ${found.length} pozīcijas`);
    navigate("/grozs");
  };

  const foundTotal = rows.filter((r) => r.status === "found" && r.product)
    .reduce((s, r) => s + tierPrice(r.product.price_tiers, r.qty) * r.qty, 0);

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <div className="flex items-center gap-3">
        <Lightning size={32} weight="fill" className="text-[#2E86D1]" />
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Ātrā pasūtīšana</h1>
      </div>
      <p className="mt-2 text-sm text-slate-600">Zināt artikulu numurus? Ievadiet tos sarakstā ar daudzumiem un pievienojiet visu grozam ar vienu klikšķi. Ideāli servisiem un pastāvīgajiem klientiem.</p>

      <div className="mt-6 rounded-2xl overflow-hidden border border-slate-300">
        <div className="grid grid-cols-[1fr_90px_1fr_36px] gap-3 bg-[#1D1D1F] text-white px-4 py-2.5 text-xs font-semibold tracking-wide">
          <span>Artikuls (SKU)</span><span>Daudz.</span><span>Statuss</span><span />
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_90px_1fr_36px] gap-3 items-center px-4 py-3 border-b border-border last:border-b-0">
            <input data-testid={`quickorder-sku-${i}`} value={r.sku} placeholder="piem. ED-3068"
              onChange={(e) => setRow(i, { sku: e.target.value, status: null, product: null })}
              className="border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <input data-testid={`quickorder-qty-${i}`} type="number" min="1" value={r.qty}
              onChange={(e) => setRow(i, { qty: Math.max(1, parseInt(e.target.value) || 1) })}
              className="border border-border px-2 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <div className="text-xs min-w-0" data-testid={`quickorder-status-${i}`}>
              {r.status === "found" && (
                <div>
                  <p className="font-semibold text-[#16A34A] line-clamp-1">{r.product.name}</p>
                  <p className="text-slate-500">{formatPrice(tierPrice(r.product.price_tiers, r.qty))}/gab · kopā {formatPrice(tierPrice(r.product.price_tiers, r.qty) * r.qty)}</p>
                </div>
              )}
              {r.status === "notfound" && <p className="font-semibold text-[#2E86D1]">Artikuls nav atrasts</p>}
              {!r.status && r.sku && <p className="text-slate-400">Nav pārbaudīts</p>}
            </div>
            <button data-testid={`quickorder-remove-${i}`} aria-label="Dzēst rindu" onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-[#2E86D1]"><Trash size={16} /></button>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button data-testid="quickorder-add-row-btn" onClick={() => setRows((p) => [...p, emptyRow()])}
          className="btn-industrial inline-flex items-center gap-1.5 border border-slate-300 px-4 py-2.5 text-xs font-semibold hover:bg-[#2E86D1] hover:text-white hover:border-[#2E86D1]">
          <Plus size={16} /> Pievienot rindu
        </button>
        <button data-testid="quickorder-check-btn" onClick={check} disabled={checking}
          className="btn-industrial px-4 py-2.5 text-xs font-semibold bg-[#1D1D1F] text-white hover:bg-[#2E86D1] disabled:opacity-50">
          {checking ? "Pārbauda..." : "Pārbaudīt artikulus"}
        </button>
        <div className="ml-auto flex items-center gap-4">
          {foundTotal > 0 && <span className="text-sm font-bold font-heading" data-testid="quickorder-total">{formatPrice(foundTotal)} bez PVN</span>}
          <button data-testid="quickorder-add-to-cart-btn" onClick={addAll}
            className="btn-industrial inline-flex items-center gap-2 bg-[#2E86D1] px-5 py-2.5 text-xs font-semibold tracking-wide text-white hover:bg-[#1F6DB2]">
            <ShoppingCart size={16} /> Pievienot grozam
          </button>
        </div>
      </div>

      <div className="mt-8 border border-border bg-[#F4F5F7] p-5 text-sm text-slate-600">
        <p className="font-bold text-[#1D1D1F] uppercase text-xs tracking-wider mb-2">Piemēri testēšanai</p>
        <p className="font-mono text-xs">ED-3068 (AdBlue 10L) · ED-121 (WD-40 400ml) · ED-5037 (AGRO U 205L) · ED-1258 (PHILIPS H7) · ED-221 (POLAR antifrīzs 5L)</p>
      </div>
    </div>
  );
}
